import CloseButton from '@atoms/CloseButton/CloseButton';
import { ISiteNotification } from '@models/ISiteNotification';
import StickyNavPosition from '@models/enums/StickyNavPositionEnum';
import NotificationCarousel from '@molecules/NotificationCarousel/NotificationCarousel';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useStickyNavPositionStore from '@state/StickyNavStore';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly siteNotifications: ISiteNotification[];
}

const SiteNotifications = ({ siteNotifications }: Props) => {
  const [containerRef, { height: containerHeight }] = useResizeObserver();
  const isTouchDevice = useIsTouchDevice();
  const isMobile = useMediaQuery(768);
  const isTablet = useMediaQuery(1024);
  const { stickyNavPosition } = useStickyNavPositionStore((state) => ({
    stickyNavPosition: state.stickyNavPosition,
  }));
  const { setSiteNotificationsHeight } = useComponentsHeightsStore((state) => ({
    setSiteNotificationsHeight: state.setSiteNotificationsHeight,
  }));
  const { closeSiteNotificationAriaLabel, siteNotificationsAriaLabel } =
    useAriaLabels();
  const [isNotificationDismissed, setIsNotificationDismissed] = useState(false);
  const isStickyNavOnTop =
    stickyNavPosition === StickyNavPosition.Top ||
    (!isMobile && stickyNavPosition === StickyNavPosition.Bottom);
  const hasStickySiteNotifications = siteNotifications.length > 0 && !isTablet;
  const shouldUpdateStickyNavsPositions =
    isStickyNavOnTop && hasStickySiteNotifications;

  const notificationsBannerColour = siteNotifications[0]?.notificationColour;
  const hasDismissButton =
    siteNotifications.length === 1 && siteNotifications[0]?.notificationDismiss;

  const dismissNotification = () => {
    const dismissedNotification = siteNotifications[0];
    if (!dismissedNotification) return;

    const cookieExpiryDate = dayjs(dismissedNotification.expiryDate).diff(
      dayjs(),
      'day'
    );
    Cookies.set(dismissedNotification.notificationId, 'true', {
      expires: cookieExpiryDate,
    });
    setIsNotificationDismissed(true);
  };

  useEffect(() => {
    const anchorMenu = document.getElementById('AnchorMenu');
    const productNavigation = document.getElementById('ProductNavigation');
    const sectionNavigation = document.getElementById('SectionNavigation');

    if (anchorMenu)
      anchorMenu.style.top = shouldUpdateStickyNavsPositions
        ? `${containerHeight}px`
        : '';

    if (productNavigation) {
      productNavigation.style.top = shouldUpdateStickyNavsPositions
        ? `${containerHeight}px`
        : '';
    }

    if (sectionNavigation) {
      sectionNavigation.style.top = hasStickySiteNotifications
        ? `${containerHeight}px`
        : '';
    }
  }, [
    containerHeight,
    hasStickySiteNotifications,
    shouldUpdateStickyNavsPositions,
  ]);

  useEffect(() => {
    setSiteNotificationsHeight(containerHeight);
  }, [containerHeight, setSiteNotificationsHeight]);

  return (
    <AnimatePresence>
      {!isNotificationDismissed && siteNotifications.length > 0 && (
        <motion.div
          key="siteNotifications"
          ref={containerRef}
          className={classNames(
            'relative z-100 flex w-full items-center text-black-100',
            {
              'bg-yellow': notificationsBannerColour === 'yellow',
              'bg-grey-100': notificationsBannerColour === 'grey',
            }
          )}
          id="SiteNotifications"
          data-testid="CT-03-Site-Notification"
          initial={{
            y: '-100%',
            opacity: 0,
          }}
          animate={{
            y: '0%',
            opacity: 1,
            transition: {
              duration: 0.8,
              ease: 'easeInOut',
            },
          }}
          exit={{
            height: '0px',
            opacity: 0,
            transition: {
              duration: 0.8,
              ease: 'easeInOut',
            },
          }}
          role="region"
          aria-label={siteNotificationsAriaLabel}
        >
          <NotificationCarousel
            id="site-notifications-carousel"
            hasMultipleItems={siteNotifications.length > 1}
            isTouchDevice={isTouchDevice}
          >
            {siteNotifications.map((notification, index) => {
              const { text, link } = notification;

              return (
                <SwiperSlide
                  className="stretch-card"
                  key={notification.notificationId}
                >
                  <a
                    href={link?.url}
                    target={link?.target}
                    className={classNames(
                      'flex h-full items-center justify-center px-[4.625rem] py-2 text-center',
                      {
                        'pointer-events-none': !link?.url,
                      }
                    )}
                    data-testid={`CT-03-Site-Notification-Item-${index}`}
                    onClick={() =>
                      updateDataLayer({
                        event: 'promotionalBanner',
                        promotion: text,
                      })
                    }
                  >
                    <div
                      className="body-four"
                      data-testid="CT-03-Site-Notification-Text"
                    >
                      {text.length > 100
                        ? `${text.substring(0, 100)}...`
                        : `${text} `}

                      {link?.url && link?.name && (
                        <span
                          className="whitespace-nowrap underline"
                          data-testid="CT-03-Site-Notification-Link-Name"
                        >
                          {link.name}
                        </span>
                      )}
                    </div>
                  </a>
                </SwiperSlide>
              );
            })}
          </NotificationCarousel>

          {hasDismissButton && (
            <CloseButton
              size="1.5rem"
              onClick={dismissNotification}
              className="absolute right-1 z-10 lg:right-[1.625rem]"
              ariaLabel={closeSiteNotificationAriaLabel}
              dataTestId="CT-03-Site-Notification-Close-Button"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SiteNotifications;
